import * as CONSTRUCTOR from '../../generate';
import * as Fragments from './fragments';

/**
 * The deposit payments on the Showroom booking behind this opportunity.
 *
 * Resolved by opportunity, not by booking: the CRM does not know the Showroom booking id (the
 * link is `bookings.lead_opportunity_id` on the Showroom side and the CRM's opportunity type does
 * not expose it), so the proxy does the lookup.
 *
 * `opportunity_id` is the only argument sent. The proxy declares
 * `fetchBookingDeposit(booking_id: String, opportunity_id: String)` and **no `company_id`** —
 * the company is injected by the resolver's VTL from the Cognito claims, and an argument AppSync
 * has not declared fails validation for the whole request rather than being ignored. The
 * `booking_id` mode exists for a caller that already holds the Showroom booking; this one does
 * not, so it is left out.
 *
 * Read-only. There is no deposit mutation in the CRM and there must not be one — deposits
 * are added, approved and cancelled in Showroom.
 */
export const FETCH_BOOKING_DEPOSIT = (props = {}) => {
  const defaultProps = {
    type: 'query',
    name: 'fetchBookingDeposit',
    operationName: 'fetchBookingDeposit',
    args: [
      { name: 'opportunity_id', type: 'String', required: true },
    ],
    argsMap: [
      { name: 'opportunity_id', value: 'opportunity_id' },
    ],
    fields: '{...FetchBookingDepositResponseFields}',
    fragments: [Fragments.fetchBookingDepositResponse],
  };
  let options = defaultProps;
  if (props) options = Object.assign(defaultProps, props);
  return CONSTRUCTOR.GRAPHQL(options);
};

/**
 * One approved deposit payment's receipt PDF.
 *
 * Addressed by Showroom booking id and payment id, which is what the proxy's
 * `fetchBookingDepositReceipt(booking_id!, payment_id!)` takes: unlike the deposit read above
 * there is no opportunity-id mode, because by the time a receipt can be asked for the panel is
 * already holding the payment row the deposit read returned, and that row carries its
 * `booking_id`.
 *
 * Still read-only, and still not a link: see the note on
 * `fetchBookingDepositReceiptResponse` in ./fragments.js for why the PDF has to come back as a
 * payload rather than as an `href`.
 */
export const FETCH_BOOKING_DEPOSIT_RECEIPT = (props = {}) => {
  const defaultProps = {
    type: 'query',
    name: 'fetchBookingDepositReceipt',
    operationName: 'fetchBookingDepositReceipt',
    args: [
      { name: 'booking_id', type: 'String', required: true },
      { name: 'payment_id', type: 'String', required: true },
    ],
    argsMap: [
      { name: 'booking_id', value: 'booking_id' },
      { name: 'payment_id', value: 'payment_id' },
    ],
    fields: '{...FetchBookingDepositReceiptResponseFields}',
    fragments: [Fragments.fetchBookingDepositReceiptResponse],
  };
  let options = defaultProps;
  if (props) options = Object.assign(defaultProps, props);
  return CONSTRUCTOR.GRAPHQL(options);
};
